import React, { useState } from 'react';
import { Form, message, Modal } from 'antd';
import * as airwayBillService from '../../services/airwayBillService';
import AirwayBillList from './airwayBill/AirwayBillList';
import AirwayBillForm from './airwayBill/AirwayBillForm';
import AirwayBillDetail from './airwayBill/AirwayBillDetail';
import { useAirwayBillData } from '../../hooks/airwayBill/useAirwayBillData';
import { useAirwayBillForm } from '../../hooks/airwayBill/useAirwayBillForm';

const AirwayBillManagement = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [form] = Form.useForm();
  const [selectedAirwayBill, setSelectedAirwayBill] = useState(null);
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const [airwayBillOrders, setAirwayBillOrders] = useState([]);
  const [ordersPagination, setOrdersPagination] = useState({ total: 0, page: 1, pageSize: 2000 });
  const [searchParams, setSearchParams] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const {
    airwayBills,
    batches,
    ports,
    customers,
    loading,
    refetchAirwayBills
  } = useAirwayBillData(searchParams);

  const { setFormValues, handleSave } = useAirwayBillForm(form, refetchAirwayBills);

  const fetchAirwayBillOrders = async (airwayBillId, params = {}) => {
    try {
      setOrdersLoading(true);
      const data = await airwayBillService.fetchAirwayBillOrders(airwayBillId, {
        page: params.page || 1,
        pageSize: params.pageSize || 2000
      });
      setAirwayBillOrders(data.results || []);
      setOrdersPagination({
        total: data.count ?? data.total ?? (Array.isArray(data.results) ? data.results.length : 0),
        page: params.page || 1,
        pageSize: params.pageSize || 2000
      });
    } catch (error) {
      console.error('Error fetching airway bill orders:', error);
      message.error('获取空运单订单列表失败');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingBill(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingBill(record);
    setFormValues(record);
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个空运单吗？',
      onOk: async () => {
        try {
          await airwayBillService.deleteAirwayBill(record.id);
          message.success('空运单删除成功');
          refetchAirwayBills();
        } catch (error) {
          console.error('Error deleting airway bill:', error);
          message.error('删除空运单失败');
        }
      },
    });
  };

  const handleViewDetails = (record) => {
    // Set airwaybill data immediately
    setSelectedAirwayBill(record);
    setEditingBill(record);
    setFormValues(record);
    setIsViewingDetails(true);
    
    // Then fetch orders asynchronously
    fetchAirwayBillOrders(record.id);
  };

  const handleReturn = () => {
    setIsViewingDetails(false);
    setSelectedAirwayBill(null);
    setEditingBill(null);
    setAirwayBillOrders([]);
    form.resetFields();
    // Refresh the airway bill list when returning
    refetchAirwayBills();
  };

  const handleFormSave = async () => {
    await handleSave(isViewingDetails, selectedAirwayBill, editingBill, () => {
      if (!isViewingDetails) {
        setModalVisible(false);
      } else {
        fetchAirwayBillOrders(selectedAirwayBill.id);
      }
    });
  };

  if (isViewingDetails) {
    return (
      <AirwayBillDetail
        form={form}
        selectedAirwayBill={selectedAirwayBill}
        airwayBillOrders={airwayBillOrders}
        loading={ordersLoading}
        ports={ports}
        customers={customers}
        batches={batches}
        onSave={handleFormSave}
        onReturn={handleReturn}
        selectedOrder={selectedOrder}
        orderModalVisible={orderModalVisible}
        onOrderClick={(order) => {
          setSelectedOrder(order);
          setOrderModalVisible(true);
        }}
        setOrderModalVisible={setOrderModalVisible}
        onOrdersUpdate={(params) => {
          console.log('Updating orders in AirwayBillManagement:', { 
            params,
            selectedAirwayBill: selectedAirwayBill?.id 
          });
          
          if (Array.isArray(params)) {
            // If array is provided, these are filtered results
            setAirwayBillOrders(params);
            setOrdersPagination(prev => ({ ...prev, total: params.length }));
          } else if (selectedAirwayBill) {
            // Otherwise fetch with pagination params
            fetchAirwayBillOrders(selectedAirwayBill.id, params);
          }
        }}
        onSearch={(params) => setSearchParams(params)}
        totalOrders={ordersPagination.total}
        pagination={{
          total: ordersPagination.total,
          current: ordersPagination.page,
          pageSize: ordersPagination.pageSize,
          pageSizeOptions: [500, 1000, 2000],
          showSizeChanger: true
        }}
      />
    );
  }

  return (
    <>
      <AirwayBillList
        airwayBills={airwayBills}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreate={handleCreate}
        onRowClick={handleViewDetails}
        onSearch={setSearchParams}
        searchText={searchParams.searchText || ''}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        customers={customers}
        selectedCustomer={searchParams.customer}
        onCustomerChange={(customer) => setSearchParams(prev => ({ ...prev, customer }))}
      />

      <AirwayBillForm
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleFormSave}
        form={form}
        editingBill={editingBill}
        batches={batches}
        loading={loading}
        ports={ports}
        customers={customers}
      />
    </>
  );
};

export default AirwayBillManagement;
